import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { FilterState } from './types';
import { emptyFilters } from './constants';
import { sanitizeFilters } from './filter';
import { filtersFromParams, hasFilterParams } from './url-state';

type Ctx = {
  filters: FilterState;
  setFilters: Dispatch<SetStateAction<FilterState>>;
  reset: () => void;
};

const FiltersContext = createContext<Ctx | null>(null);
const LS_KEY = 'spark-board.filters';

/**
 * Initial filters: a shared link's query params win (so opening someone's link reproduces
 * their filters), else the last persisted set, else empty. Coerced to a valid FilterState.
 */
const loadFilters = (): FilterState => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (hasFilterParams(params)) return filtersFromParams(params);
  } catch {
    /* ignore unavailable location */
  }
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return sanitizeFilters(JSON.parse(raw));
  } catch {
    /* ignore malformed / unavailable storage */
  }
  return emptyFilters();
};

export const useFilters = (): Ctx => {
  const v = useContext(FiltersContext);
  if (!v) throw new Error('useFilters must be used within FiltersProvider');
  return v;
};

/**
 * Search + filter state shared by the Table and Kanban views. Lifting it here (rather
 * than owning it per-page) is what keeps the two views in sync: switch tabs and the
 * same query still applies. Persisted to localStorage so it also survives a reload.
 */
export const FiltersProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<FilterState>(loadFilters);

  // When the initial filters came from a shared link's query params, DON'T persist that seed —
  // opening someone's link shouldn't overwrite the recipient's own saved default. A later,
  // user-driven change then persists as normal. Guards only the first (mount) effect run.
  const skipFirstPersist = useRef<boolean>(
    (() => {
      try {
        return hasFilterParams(new URLSearchParams(window.location.search));
      } catch {
        return false;
      }
    })(),
  );

  useEffect(() => {
    if (skipFirstPersist.current) {
      skipFirstPersist.current = false;
      return;
    }
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(filters));
    } catch {
      /* ignore unavailable storage */
    }
  }, [filters]);

  const value = useMemo<Ctx>(
    () => ({ filters, setFilters, reset: () => setFilters(emptyFilters()) }),
    [filters],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};
